/**
 * Analytics chart palette & shade matrix — single source of truth.
 *
 * Edit `CHART_COLORS` here: the Foundations → Color page (shade matrix) and the
 * Charts design-system page both consume this module. Re-exported from `ColorsPage`
 * for discoverability alongside the Color docs.
 */
export const CHART_COLORS = {
  green: '#00A76F',
  amber: '#FFAB00',
  cyan: '#00B8D9',
  orange: '#F97316',
  violet: '#8B5CF6',
  teal: '#00B8A3',
  blue: '#3366FF',
  rose: '#FF4081',
} as const;

export const CHART_PALETTE_ENTRIES = Object.entries(CHART_COLORS) as [string, string][];

/** Shade-matrix order: 1st series = green, 2nd = amber, 3rd = cyan, … through rose. */
export const CHART_PALETTE_ORDER = [
  CHART_COLORS.green,
  CHART_COLORS.amber,
  CHART_COLORS.cyan,
  CHART_COLORS.orange,
  CHART_COLORS.violet,
  CHART_COLORS.teal,
  CHART_COLORS.blue,
  CHART_COLORS.rose,
] as const;

export function shadeHex(hex: string, target: string, t: number): string {
  const parse = (s: string) => [parseInt(s.slice(1, 3), 16), parseInt(s.slice(3, 5), 16), parseInt(s.slice(5, 7), 16)];
  const [r1, g1, b1] = parse(hex);
  const [r2, g2, b2] = parse(target);
  const m = (a: number, b: number) => Math.round(a + (b - a) * t).toString(16).padStart(2, '0');
  return `#${m(r1, r2)}${m(g1, g2)}${m(b1, b2)}`;
}

/** Same order as CHART_PALETTE_ORDER; each hue at the shade matrix “Medium” step (28% toward white). */
export const CHART_PALETTE_ORDER_MEDIUM: readonly string[] = CHART_PALETTE_ORDER.map((c) =>
  shadeHex(c, '#FFFFFF', 0.28),
);

export const CHART_SHADE_LEVELS = [
  { label: 'Lightest', shade: (c: string) => shadeHex(c, '#FFFFFF', 0.84) },
  { label: 'Light', shade: (c: string) => shadeHex(c, '#FFFFFF', 0.56) },
  { label: 'Medium', shade: (c: string) => shadeHex(c, '#FFFFFF', 0.28) },
  { label: 'Base', shade: (c: string) => c },
  { label: 'Dark', shade: (c: string) => shadeHex(c, '#000000', 0.25) },
] as const;

/** Deterministic palette color for a label (e.g. assessment type) — stable across renders and pages. */
export function stablePaletteColorForKey(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (Math.imul(31, hash) + key.charCodeAt(i)) >>> 0;
  }
  return CHART_PALETTE_ORDER[hash % CHART_PALETTE_ORDER.length];
}
